// src/pages/AdminLogin.js
import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaUserShield, FaLock, FaSignInAlt, FaEye, FaEyeSlash, FaHospital, FaUser, FaShieldAlt, FaKey, FaUserCircle } from 'react-icons/fa';

// Import the logo - Correct path from your structure
import logo from '../media/logo.png';

const AdminLogin = () => {
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        rememberMe: false
    });
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Demo credentials
    const DEMO_CREDENTIALS = {
        username: 'admin',
        password: 'password'
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.username.trim()) {
            errors.username = 'Username is required';
        }
        if (!formData.password) {
            errors.password = 'Password is required';
        }
        return errors;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
        if (formErrors[name]) {
            setFormErrors({
                ...formErrors,
                [name]: null
            });
        }
        setError('');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const errors = validateForm();

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        // Use demo credentials
        if (formData.username === DEMO_CREDENTIALS.username && formData.password === DEMO_CREDENTIALS.password) {
            alert('Login successful! Redirecting to admin dashboard...');
        } else {
            setError(`Invalid credentials. Use ${DEMO_CREDENTIALS.username}/${DEMO_CREDENTIALS.password} for demo.`);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <section className="admin-login-section" style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f9ff 50%, #e6f2ff 100%)',
            padding: '60px 15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Decorative Background Elements */}
            <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '300px',
                height: '300px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(26, 106, 122, 0.05) 0%, rgba(26, 106, 122, 0) 70%)',
                zIndex: 0
            }}></div>
            <div style={{
                position: 'absolute',
                bottom: '-100px',
                left: '-100px',
                width: '400px',
                height: '400px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(13, 69, 80, 0.03) 0%, rgba(13, 69, 80, 0) 70%)',
                zIndex: 0
            }}></div>

            <Container style={{ position: 'relative', zIndex: 1 }}>
                <Row className="justify-content-center align-items-center">
                    <Col xl={4} lg={5} md={6} sm={8} xs={12}>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <Card className="border-0 shadow-lg" style={{
                                borderRadius: '20px',
                                overflow: 'hidden',
                                border: 'none',
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)',
                                boxShadow: '0 20px 60px rgba(13, 69, 80, 0.1), 0 5px 20px rgba(13, 69, 80, 0.05)'
                            }}>
                                {/* Header with Logo - REDUCED MARGIN */}
                                <div style={{
                                    background: 'linear-gradient(135deg, rgba(26, 106, 122, 0.1) 0%, rgba(13, 69, 80, 0.05) 100%)',
                                    padding: '1.5rem 1rem 0.5rem',
                                    textAlign: 'center',
                                    position: 'relative'
                                }}>
                                    {/* Logo Container - VERY LARGE LOGO */}
                                    <div className="d-flex flex-column align-items-center">
                                        <div style={{
                                            width: '180px',
                                            height: '180px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'visible',
                                            borderRadius: '18px',
                                            backgroundColor: 'transparent',
                                            padding: '0',
                                            marginBottom: '2px', // REDUCED FROM 15px TO 2px
                                            border: 'none'
                                        }}>
                                            {logo ? (
                                                <img
                                                    src={logo}
                                                    alt="Lotus Hospital Logo"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        filter: 'drop-shadow(0 8px 16px rgba(26, 106, 122, 0.6))'
                                                    }}
                                                    onError={(e) => {
                                                        console.log('Logo image failed to load:', logo);
                                                        e.target.style.display = 'none';
                                                        e.target.parentElement.innerHTML = `
                                                            <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; border-radius: 18px;">
                                                                <div style="
                                                                    width: 150px;
                                                                    height: 150px;
                                                                    background: linear-gradient(135deg, #1a6a7a 0%, #0d4550 100%);
                                                                    border-radius: 18px;
                                                                    display: flex;
                                                                    align-items: center;
                                                                    justify-content: center;
                                                                    box-shadow: 0 12px 35px rgba(26, 106, 122, 0.7);
                                                                ">
                                                                    <FaHospital style="font-size: 4.5rem; color: white;" />
                                                                </div>
                                                            </div>
                                                        `;
                                                    }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: '150px',
                                                    height: '150px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: 'linear-gradient(135deg, #1a6a7a 0%, #0d4550 100%)',
                                                    borderRadius: '18px',
                                                    boxShadow: '0 12px 35px rgba(26, 106, 122, 0.7)'
                                                }}>
                                                    <FaHospital style={{ fontSize: '4.5rem', color: 'white' }} />
                                                </div>
                                            )}
                                        </div>

                                        {/* Admin Portal Title - CLOSER TO LOGO */}
                                        <div className="text-center" style={{ marginTop: '0px' }}>
                                            <h3 className="fw-bold mb-1" style={{
                                                color: '#0d4550',
                                                fontSize: '1.7rem'
                                            }}>
                                                <FaShieldAlt className="me-2" style={{
                                                    color: '#1a6a7a',
                                                    filter: 'drop-shadow(0 2px 3px rgba(14, 26, 29, 0.3))',
                                                    fontSize: '1.5rem'
                                                }} />
                                                Admin Portal
                                            </h3>

                                            <p className="mb-0" style={{
                                                color: '#666',
                                                fontSize: '1rem',
                                                fontWeight: '400',
                                                opacity: 0.8,
                                                marginTop: '2px'
                                            }}>
                                                Secure Hospital Management System
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <Card.Body className="p-3 p-md-4">
                                    {error && (
                                        <Alert variant="danger" className="text-center py-2 mb-3" style={{
                                            borderRadius: '10px',
                                            fontSize: '0.85rem',
                                            border: 'none',
                                            background: 'linear-gradient(135deg, rgba(255, 234, 234, 0.9) 0%, rgba(255, 204, 204, 0.9) 100%)',
                                            backdropFilter: 'blur(5px)',
                                            border: '1px solid #ff9999',
                                            color: '#721c24',
                                            boxShadow: '0 4px 12px rgba(220, 53, 69, 0.1)',
                                            padding: '12px'
                                        }}>
                                            {error}
                                        </Alert>
                                    )}

                                    <Form onSubmit={handleSubmit}>
                                        {/* Pre-filled Email Field */}
                                        <Form.Group className="mb-3">
                                            <div className="position-relative">
                                                <div style={{
                                                    position: 'absolute',
                                                    left: '15px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    color: '#1a6a7a',
                                                    zIndex: 2,
                                                    opacity: 0.8
                                                }}>
                                                    <FaUser />
                                                </div>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={formData.username}
                                                    placeholder='Enter your username'
                                                    className="py-2"
                                                    style={{
                                                        borderRadius: '10px',
                                                        border: '2px solid rgba(222, 226, 230, 0.7)',
                                                        paddingLeft: '50px',
                                                        fontSize: '0.95rem',
                                                        height: '52px',
                                                        transition: 'all 0.3s ease',
                                                        backgroundColor: 'rgba(248, 249, 250, 0.8)',
                                                        color: '#495057',
                                                        fontWeight: '500',
                                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)'
                                                    }}
                                                />
                                            </div>
                                        </Form.Group>

                                        {/* Password Field */}
                                        <Form.Group className="mb-3">
                                            <div className="position-relative">
                                                <div style={{
                                                    position: 'absolute',
                                                    left: '15px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    color: formErrors.password ? '#dc3545' : '#1a6a7a',
                                                    zIndex: 2,
                                                    opacity: 0.8
                                                }}>
                                                    <FaLock />
                                                </div>
                                                <Form.Control
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    placeholder="Enter your password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    className={`py-2 ${formErrors.password ? 'is-invalid' : ''}`}
                                                    style={{
                                                        borderRadius: '10px',
                                                        border: `2px solid ${formErrors.password ? 'rgba(220, 53, 69, 0.5)' : 'rgba(222, 226, 230, 0.7)'}`,
                                                        paddingLeft: '50px',
                                                        paddingRight: '50px',
                                                        fontSize: '0.95rem',
                                                        height: '52px',
                                                        letterSpacing: showPassword ? 'normal' : '2px',
                                                        transition: 'all 0.3s ease',
                                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
                                                        backgroundColor: 'white'
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={togglePasswordVisibility}
                                                    style={{
                                                        position: 'absolute',
                                                        right: '15px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        background: 'transparent',
                                                        border: 'none',
                                                        color: '#1a6a7a',
                                                        padding: '0',
                                                        cursor: 'pointer',
                                                        zIndex: 2,
                                                        fontSize: '1.1rem',
                                                        transition: 'all 0.2s ease',
                                                        opacity: 0.7
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.color = '#0d4550';
                                                        e.target.style.opacity = '1';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.color = '#1a6a7a';
                                                        e.target.style.opacity = '0.7';
                                                    }}
                                                >
                                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                </button>
                                            </div>
                                            {formErrors.password && (
                                                <div className="text-danger mt-1 ps-3" style={{ fontSize: '0.8rem' }}>
                                                    {formErrors.password}
                                                </div>
                                            )}
                                        </Form.Group>

                                        {/* Remember Me & Forgot Password */}
                                        <div className="mb-3 d-flex justify-content-between align-items-center">
                                            <Form.Check
                                                type="checkbox"
                                                id="rememberMe"
                                                name="rememberMe"
                                                label={
                                                    <span style={{
                                                        color: '#555',
                                                        fontSize: '0.9rem',
                                                        cursor: 'pointer',
                                                        fontWeight: '500'
                                                    }}>
                                                        Remember me
                                                    </span>
                                                }
                                                checked={formData.rememberMe}
                                                onChange={handleChange}
                                                style={{
                                                    accentColor: '#1a6a7a'
                                                }}
                                            />
                                            <a
                                                href="#forgot"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    alert('Password reset link would be sent to harrypotter@gmail.com');
                                                }}
                                                style={{
                                                    color: '#1a6a7a',
                                                    textDecoration: 'none',
                                                    fontSize: '0.9rem',
                                                    fontWeight: '500',
                                                    transition: 'all 0.2s ease',
                                                    opacity: 0.8
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.color = '#0d4550';
                                                    e.target.style.textDecoration = 'underline';
                                                    e.target.style.opacity = '1';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.color = '#1a6a7a';
                                                    e.target.style.textDecoration = 'none';
                                                    e.target.style.opacity = '0.8';
                                                }}
                                            >
                                                Forget Password?
                                            </a>
                                        </div>

                                        {/* Login Button */}
                                        <motion.div
                                            whileHover={{ scale: 1.02, y: -2 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <Button
                                                type="submit"
                                                className="w-100 fw-bold d-flex align-items-center justify-content-center"
                                                style={{
                                                    height: '52px',
                                                    borderRadius: '10px',
                                                    fontSize: '1rem',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    background: 'linear-gradient(135deg, #1a6a7a 0%, #0d4550 100%)',
                                                    border: 'none',
                                                    color: 'white',
                                                    boxShadow: '0 6px 20px rgba(26, 106, 122, 0.3)',
                                                    marginBottom: '15px'
                                                }}
                                            >
                                                <FaSignInAlt className="me-2" />
                                                Login to Dashboard
                                            </Button>
                                        </motion.div>

                                        {/* Demo Credentials Section - MODERN & ATTRACTIVE */}
                                        <div className="mt-3 pt-3 text-center" style={{
                                            borderTop: '1px dashed rgba(26, 106, 122, 0.2)',
                                            background: 'linear-gradient(135deg, rgba(248, 249, 250, 0.5) 0%, rgba(233, 236, 239, 0.3) 100%)',
                                            borderRadius: '10px',
                                            padding: '1rem',
                                            backdropFilter: 'blur(5px)'
                                        }}>
                                            <h6 className="mb-2 d-flex align-items-center justify-content-center" style={{
                                                color: '#0d4550',
                                                fontWeight: '600',
                                                fontSize: '0.95rem'
                                            }}>
                                                <FaKey className="me-2" style={{ fontSize: '0.9rem' }} />
                                                Demo Credentials
                                            </h6>
                                            <div className="d-flex justify-content-center align-items-center gap-2">
                                                <div style={{
                                                    background: 'white',
                                                    padding: '10px 15px',
                                                    borderRadius: '8px',
                                                    border: '1px solid rgba(222, 226, 230, 0.4)',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                                    transition: 'all 0.3s ease',
                                                    minWidth: '100px',
                                                    textAlign: 'center'
                                                }}>
                                                    <div className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                                                        <FaUserCircle className="me-1" style={{ fontSize: '0.8rem' }} />
                                                        Username
                                                    </div>
                                                    <div className="fw-bold" style={{
                                                        color: '#0d4550',
                                                        fontSize: '1.1rem',
                                                        letterSpacing: '0.5px'
                                                    }}>
                                                        admin
                                                    </div>
                                                </div>
                                                <div style={{
                                                    background: 'white',
                                                    padding: '10px 15px',
                                                    borderRadius: '8px',
                                                    border: '1px solid rgba(222, 226, 230, 0.4)',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                                    transition: 'all 0.3s ease',
                                                    minWidth: '100px',
                                                    textAlign: 'center'
                                                }}>
                                                    <div className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                                                        <FaKey className="me-1" style={{ fontSize: '0.8rem' }} />
                                                        Password
                                                    </div>
                                                    <div className="fw-bold" style={{
                                                        color: '#0d4550',
                                                        fontSize: '1.1rem',
                                                        letterSpacing: '0.5px'
                                                    }}>
                                                        password
                                                    </div>
                                                </div>
                                            </div>
                                            <small className="text-muted mt-2 d-block" style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                                                Use these credentials for testing
                                            </small>
                                        </div>

                                        {/* Security Footer */}
                                        <div className="mt-3 pt-2 text-center">
                                            <p className="mb-1" style={{
                                                fontSize: '0.75rem',
                                                color: '#666',
                                                lineHeight: '1.4',
                                                opacity: 0.7
                                            }}>
                                                Access restricted to authorized personnel only. All activities are monitored.
                                            </p>
                                            <p className="mb-0" style={{
                                                fontSize: '0.7rem',
                                                color: '#888',
                                                fontStyle: 'italic',
                                                opacity: 0.6
                                            }}>
                                                © {new Date().getFullYear()} Lotus Hospital
                                            </p>
                                        </div>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </motion.div>
                    </Col>
                </Row>
            </Container>

            {/* Responsive Styles */}
            <style jsx="true">{`
                .form-control:focus {
                    border-color: #1a6a7a !important;
                    box-shadow: 0 0 0 0.2rem rgba(26, 106, 122, 0.25) !important;
                    background-color: white !important;
                }
                
                .form-control::placeholder {
                    color: #6c757d;
                    opacity: 0.7;
                }
                
                /* Fix for Firefox checkbox */
                input[type="checkbox"] {
                    accent-color: #1a6a7a !important;
                }
                
                /* Hover effects for demo credentials */
                .d-flex.justify-content-center.align-items-center.gap-2 > div:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(13, 69, 80, 0.1) !important;
                    border-color: rgba(26, 106, 122, 0.3) !important;
                }
                
                /* Login button hover effect */
                .btn-primary:hover {
                    background: linear-gradient(135deg, #0d4550 0%, #1a6a7a 100%) !important;
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(26, 106, 122, 0.4) !important;
                }
                
                /* Responsive adjustments */
                @media (max-width: 1200px) {
                    .admin-login-section {
                        padding: 50px 15px;
                    }
                    .col-xl-4 {
                        max-width: 450px;
                    }
                    .d-flex.flex-column.align-items-center > div:first-child {
                        width: 160px !important;
                        height: 160px !important;
                        margin-bottom: 2px !important;
                    }
                    h3 {
                        font-size: 1.6rem !important;
                    }
                }
                
                @media (max-width: 992px) {
                    .admin-login-section {
                        padding: 40px 15px;
                    }
                    .col-lg-5 {
                        max-width: 500px;
                    }
                    .card-body {
                        padding: 1.25rem !important;
                    }
                    .d-flex.flex-column.align-items-center > div:first-child {
                        width: 150px !important;
                        height: 150px !important;
                        margin-bottom: 2px !important;
                    }
                    h3 {
                        font-size: 1.5rem !important;
                    }
                }
                
                @media (max-width: 768px) {
                    .admin-login-section {
                        padding: 30px 15px;
                    }
                    .col-md-6 {
                        max-width: 450px;
                    }
                    h3 {
                        font-size: 1.4rem !important;
                    }
                    .form-control {
                        height: 50px !important;
                        font-size: 0.9rem !important;
                    }
                    button[type="submit"] {
                        height: 50px !important;
                        font-size: 0.95rem !important;
                    }
                    .d-flex.flex-column.align-items-center > div:first-child {
                        width: 140px !important;
                        height: 140px !important;
                        margin-bottom: 2px !important;
                    }
                }
                
                @media (max-width: 576px) {
                    .admin-login-section {
                        padding: 25px 12px;
                    }
                    .col-sm-8 {
                        max-width: 100%;
                        padding: 0 10px;
                    }
                    .card-body {
                        padding: 1rem !important;
                    }
                    h3 {
                        font-size: 1.3rem !important;
                    }
                    p {
                        font-size: 0.9rem !important;
                    }
                    .form-control {
                        height: 48px !important;
                        font-size: 0.85rem !important;
                        padding-left: 45px !important;
                        padding-right: 45px !important;
                    }
                    button[type="submit"] {
                        height: 48px !important;
                        font-size: 0.9rem !important;
                    }
                    .position-absolute {
                        left: 12px !important;
                        right: 12px !important;
                    }
                    .d-flex.flex-column.align-items-center > div:first-child {
                        width: 130px !important;
                        height: 130px !important;
                        margin-bottom: 2px !important;
                    }
                    /* Stack demo credentials vertically on mobile */
                    .d-flex.justify-content-center.align-items-center.gap-2 {
                        flex-direction: column;
                        gap: 10px !important;
                    }
                    .d-flex.justify-content-center.align-items-center.gap-2 > div {
                        width: 100%;
                        max-width: 200px;
                    }
                }
                
                @media (max-width: 375px) {
                    .admin-login-section {
                        padding: 20px 8px;
                    }
                    .card-body {
                        padding: 0.85rem !important;
                    }
                    h3 {
                        font-size: 1.2rem !important;
                    }
                    p {
                        font-size: 0.85rem !important;
                    }
                    .form-control {
                        height: 46px !important;
                        font-size: 0.8rem !important;
                    }
                    button[type="submit"] {
                        height: 46px !important;
                        font-size: 0.85rem !important;
                    }
                    .demo-credentials {
                        padding: 0.85rem !important;
                    }
                    .d-flex.flex-column.align-items-center > div:first-child {
                        width: 120px !important;
                        height: 120px !important;
                        margin-bottom: 2px !important;
                    }
                }
                
                @media (max-width: 320px) {
                    .admin-login-section {
                        padding: 15px 8px;
                    }
                    .d-flex.flex-column.align-items-center > div:first-child {
                        width: 110px !important;
                        height: 110px !important;
                        margin-bottom: 2px !important;
                    }
                    h3 {
                        font-size: 1.1rem !important;
                    }
                    p {
                        font-size: 0.8rem !important;
                    }
                }
            `}</style>
        </section>
    );
};

export default AdminLogin;